import React, { useMemo } from "react";
import Moment from "moment";
import { t } from "../../../i18n";
import { Download, Show } from "../normalizer";
import CustomDataTable from "../shared/CustomDataTable";
import CustomButton from "../shared/CustomButton";

/**
 * Wallet data table built on CustomDataTable. Each row exposes a direct
 * Download anchor for the asset and a "Ver Detalhes" action that navigates to
 * the voucher detail page.
 */
const WalletTable = (props) => {
  const { items, pagination, page, onPageChange, onViewDetails } = props;

  const formatDate = (value) =>
    value ? new Moment(value).format(t("date-format")) : "-";

  // The user vouchers endpoint nests most display fields under `voucher_type`.
  // These helpers read the nested structure with fallbacks to legacy flat keys.
  const voucherName = (item) => {
    const vt = item.voucher_type || {};
    return vt.name || (vt.product && vt.product.name) || item.name || item.description || "-";
  };

  const voucherType = (item) => {
    const vt = item.voucher_type || {};
    const category = vt.category || item.category;
    return (category && category.name) || item.type_label || item.type || "-";
  };

  const voucherDate = (item) => item.purchased_at

  const voucherExpiry = (item) => item.validity || item.expires_at;

  const voucherDownloadUrl = (item) => item.url;

  // Order and state are nested objects that may be absent on partial payloads;
  // read them through guards so the cell renders a dash instead of throwing.
  const orderNumber = (item) => (item.order && item.order.number) || "-";

  const orderPoints = (item) => {
    const points = item.order && item.order.points;
    return points !== undefined && points !== null ? points : "-";
  };

  const renderState = (item) => {
    const state = item.state || {};
    if (!state.label && !state.code) return "-";
    const code = state.code || "";
    const className = code
      ? "wallet-state-pill wallet-state-pill--" + code
      : "wallet-state-pill";
    return <span className={className}>{state.label || state.code}</span>;
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        label: t("gamification2026.wallet.table.name"),
        sortKey: "voucher_type.name",
        render: (_v, item) => voucherName(item),
      },
      {
        key: "order_number",
        label: t("gamification2026.wallet.table.order-number"),
        sortKey: "order.number",
        render: (_v, item) => orderNumber(item),
      },
      {
        key: "purchased_at",
        label: t("gamification2026.wallet.table.date"),
        sortKey: "purchased_at",
        render: (_v, item) => formatDate(voucherDate(item)),
      },
      {
        key: "validity",
        label: t("gamification2026.wallet.table.expiry"),
        sortKey: "validity",
        render: (_v, item) => formatDate(voucherExpiry(item)),
      },
      {
        key: "order_points",
        label: t("gamification2026.wallet.table.points"),
        sortKey: "order.points",
        render: (_v, item) => orderPoints(item),
      },
      {
        key: "state",
        label: t("gamification2026.wallet.table.status"),
        sortKey: "state.code",
        render: (_v, item) => renderState(item),
      },
      {
        key: "actions",
        label: t("gamification2026.wallet.table.actions"),
        sortable: false,
        align: "right",
        render: (_v, item) => (
          <div className="wallet-table-actions">
            {voucherDownloadUrl(item) ? (
              <a
                href={voucherDownloadUrl(item)}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="wallet-table-actions-download"
              >
                <Download className="wallet-table-actions-download-icon" />
                {t("gamification2026.wallet.table.download")}
              </a>
            ) : null/* (
              <div className="flex flex-row items-center justify-center">
                <Download className="wallet-table-actions-download-icon" />
                {t("gamification2026.wallet.table.download")}
              </div>
            )*/}
            <CustomButton link small onClick={() => onViewDetails(item)}>
              <Show className="wallet-table-actions-details-icon" />
              {t("gamification2026.wallet.table.view-details")}
            </CustomButton>
          </div>
        ),
      },
    ],
    [onViewDetails],
  );

  return (
    <CustomDataTable
      className="wallet-table"
      columns={columns}
      data={items}
      rowKey={(item) => item.id}
      forceCards
      pagination={{
        totalPages: pagination.totalPages,
        currentPage: page,
        onPageChange,
      }}
      emptyMessage={t("gamification2026.wallet.table.no-results")}
    />
  );
};

export default WalletTable;
